import React from 'react';
import { Box, Container, Typography, Link, Divider } from '@mui/material';
import { CookieSettingsButton } from './CookieConsent';

// ============================================================================
// FOOTER COMPONENT
// Site-wide footer with legal links and copyright
// ============================================================================

interface FooterProps {
  variant?: 'light' | 'dark';
  showCookieSettings?: boolean;
}

const Footer: React.FC<FooterProps> = ({ 
  variant = 'light',
  showCookieSettings = true 
}) => {
  const currentYear = new Date().getFullYear();
  
  const isDark = variant === 'dark';
  
  const textColor = isDark ? 'rgba(255,255,255,0.6)' : 'text.secondary';
  const linkColor = isDark ? 'rgba(255,255,255,0.8)' : 'primary.main';
  const borderColor = isDark ? 'rgba(255,255,255,0.1)' : 'divider';
  const bgColor = isDark ? 'rgba(0,0,0,0.3)' : 'transparent';

  return (
    <Box
      component="footer"
      sx={{
        py: 3,
        mt: 'auto',
        borderTop: '1px solid',
        borderColor: borderColor,
        backgroundColor: bgColor,
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {/* Copyright */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="img"
              src="/logo.svg"
              alt="Check Set Go"
              sx={{ 
                width: 24, 
                height: 28,
                opacity: isDark ? 0.8 : 1,
              }}
            />
            <Typography variant="body2" sx={{ color: textColor }}>
              © {currentYear} Centre Of Business. All rights reserved.
            </Typography>
          </Box>

          {/* Legal Links */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 2, md: 3 },
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <Link
              href="/privacy-policy"
              sx={{
                color: linkColor,
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms-of-service"
              sx={{
                color: linkColor,
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Terms of Service
            </Link>
            <Link
              href="/cookie-policy"
              sx={{
                color: linkColor,
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Cookie Policy
            </Link>
            {showCookieSettings && (
              <>
                <Divider 
                  orientation="vertical" 
                  flexItem 
                  sx={{ 
                    borderColor: borderColor,
                    display: { xs: 'none', md: 'block' }
                  }} 
                />
                <CookieSettingsButton />
              </>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

// ============================================================================
// MINIMAL FOOTER
// Compact footer for auth pages
// ============================================================================

export const MinimalFooter: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        py: 2,
        mt: 'auto',
        textAlign: 'center',
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        © {currentYear} Centre Of Business
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Link
          href="/privacy-policy"
          target="_blank"
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            fontSize: '0.75rem',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main',
            },
          }}
        >
          Privacy
        </Link>
        <Link
          href="/terms-of-service"
          target="_blank"
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            fontSize: '0.75rem',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main',
            },
          }}
        >
          Terms
        </Link>
        <Link
          href="/cookie-policy"
          target="_blank"
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            fontSize: '0.75rem',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main',
            },
          }}
        >
          Cookies
        </Link>
      </Box>
    </Box>
  );
};

export default Footer;

